import React from "react";
import {
  Smartphone, ClipboardList, Truck, AlertTriangle, ShieldCheck,
  QrCode, Camera, FileSignature, ArrowRight, Timer,
} from "lucide-react";

/**
 * FieldFirstWorkflows — the SIMPLICITY counterweight to the cinematic
 * Operations Preview. Shows the actual field workflows with step counts,
 * timing, and the automated consequence on the right side.
 *
 * Tone: tight, practical, NOT cinematic. This block is where prospects
 * realise crews will actually adopt the system.
 */
const WORKFLOWS = [
  {
    role: "Foreman",
    icon: ClipboardList,
    title: "Daily Report",
    timing: "≈ 45 sec from the truck cab",
    steps: ["Pick job", "Crew + visitors", "Weather (auto)", "Photos", "Submit"],
    consequence: "PDF generated · routed to PM Folder + Job Folder + Backup",
    consequenceColor: "#FF6A00",
    testid: "wf-daily",
  },
  {
    role: "Operator",
    icon: Truck,
    title: "Pre-Op Walk-Around",
    timing: "≈ 30 sec · photo required on FAIL",
    steps: ["Scan unit QR", "Walk-around checklist", "Photo on FAIL", "Sign + submit"],
    consequence: "FAIL → equipment LOCKED OUT · Shop queue · Safety alerted",
    consequenceColor: "#FF6A6A",
    testid: "wf-preop",
  },
  {
    role: "Crew",
    icon: QrCode,
    title: "Toolbox Talk",
    timing: "QR scan → 1-tap sign-off",
    steps: ["Scan poster QR", "Auto-fill name", "Acknowledge", "Done"],
    consequence: "Crew records updated · Safety Folder · attendance log",
    consequenceColor: "#65DA76",
    testid: "wf-toolbox",
  },
  {
    role: "Safety / PM",
    icon: AlertTriangle,
    title: "Incident · Near-Miss",
    timing: "≈ 2 min · works on phone in the field",
    steps: ["Pick severity", "What happened", "Photos", "Submit"],
    consequence: "Safety + PM + Admin notified · investigation opened",
    consequenceColor: "#FFD000",
    testid: "wf-incident",
  },
  {
    role: "All field staff",
    icon: FileSignature,
    title: "JHP Acknowledgment",
    timing: "QR → 1 tap · operator's record updated",
    steps: ["Scan JHP QR", "Auto-fill", "Acknowledge"],
    consequence: "Stored in operator's training record + Safety Folder",
    consequenceColor: "#65DA76",
    testid: "wf-jhp",
  },
];

export default function FieldFirstWorkflows() {
  return (
    <section
      data-testid="field-first-workflows"
      className="max-w-7xl mx-auto px-5 sm:px-8 py-12 sm:py-16"
    >
      <div className="mb-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
        <div className="lg:col-span-7">
          <div className="font-mono text-[11px] uppercase tracking-[0.4em] mb-2" style={{ color: "#FF6A00" }}>
            FIELD-FIRST · WORKFLOWS CREWS WILL USE
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tight leading-tight">
            Powerful underneath.{" "}
            <span style={{ color: "#FF6A00" }}>Brutally simple on top.</span>
          </h2>
          <p className="text-sm mt-3 max-w-2xl" style={{ color: "#BDBDBD" }}>
            Every workflow was built for the truck cab — not the office.
            Sub-minute submissions, photo-enforced FAIL paths, QR-driven
            sign-offs, automatic routing to PM / Safety / Job folders. No
            email. No paperwork delay. The kind of system crews actually use.
          </p>
        </div>

        <div className="lg:col-span-5 grid grid-cols-3 gap-px" style={{ background: "#1A1A1A" }}>
          <SimpleStat icon={Smartphone}  label="Mobile-first"    value="100%" />
          <SimpleStat icon={Timer}       label="Avg. submit"      value="< 1m" />
          <SimpleStat icon={Camera}      label="Photo-enforced"  value="FAIL" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: "#1A1A1A" }}>
        {WORKFLOWS.map((w) => (
          <WorkflowCard key={w.testid} w={w} />
        ))}

        {/* Closing tile — emphasises adoption, not features */}
        <div
          className="relative p-5 sm:p-6 flex flex-col justify-between gap-3 overflow-hidden"
          style={{ background: "#0E0E0E" }}
          data-testid="wf-adoption-card"
        >
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at 100% 0%, rgba(255,106,0,0.18), transparent 60%)",
            }}
          />
          <div className="relative">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: "#FF6A00" }}>
              ADOPTION REALITY
            </div>
            <h3 className="font-display text-xl font-black tracking-tight text-white leading-tight mb-2">
              Most construction software fails because the field won't use it.
            </h3>
            <p className="text-[12px] leading-relaxed" style={{ color: "#BDBDBD" }}>
              FORGEDOPS was built backwards from that problem. Every form
              fits one thumb. Every screen reads in sunlight. Every
              submission shows a consequence so the crew knows the system
              actually did something.
            </p>
          </div>
          <div className="relative flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] inline-flex items-center gap-1.5 px-2.5 py-1 rounded" style={{ background: "rgba(46,125,50,0.18)", color: "#65DA76", border: "1px solid rgba(46,125,50,0.4)" }}>
              <ShieldCheck className="w-3 h-3" />
              Field-tested
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: "#8A8A8A" }}>
              EN / ES bilingual · offline-friendly
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function WorkflowCard({ w }) {
  const Icon = w.icon;
  return (
    <div
      className="p-5 sm:p-6 flex flex-col gap-3 transition-colors hover:bg-[#141414]"
      style={{ background: "#0B0B0B" }}
      data-testid={w.testid}
    >
      {/* Header row */}
      <div className="flex items-start gap-3">
        <span
          className="inline-flex items-center justify-center w-10 h-10 rounded-md shrink-0"
          style={{ background: "rgba(255,106,0,0.12)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.4)" }}
        >
          <Icon className="w-5 h-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.25em]" style={{ color: "#8A8A8A" }}>
            {w.role}
          </div>
          <h3 className="font-display text-lg font-black tracking-tight text-white leading-tight">
            {w.title}
          </h3>
        </div>
      </div>

      {/* Timing line — operational, not marketing */}
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1.5" style={{ color: "#FFD000" }}>
        <Timer className="w-3 h-3" />
        {w.timing}
      </div>

      {/* Step chain — minimal, fast-reading */}
      <div className="flex items-center gap-1 flex-wrap">
        {w.steps.map((s, i) => (
          <React.Fragment key={s}>
            <span
              className="inline-flex items-center px-2 py-1 rounded font-mono text-[10px] uppercase tracking-[0.15em]"
              style={{ background: "#0F0F0F", color: "#FFFFFF", border: "1px solid #1F1F1F" }}
            >
              <span className="opacity-60 mr-1">{String(i + 1).padStart(2, "0")}</span>
              {s}
            </span>
            {i < w.steps.length - 1 && (
              <ArrowRight className="w-3 h-3 shrink-0" style={{ color: "#3A3A3A" }} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Consequence row — what happens automatically */}
      <div
        className="mt-1 flex items-start gap-2 px-3 py-2 rounded-md"
        style={{
          background: `${w.consequenceColor}10`,
          border: `1px solid ${w.consequenceColor}40`,
        }}
      >
        <span
          className="font-mono text-[9px] uppercase tracking-[0.25em] shrink-0 mt-0.5"
          style={{ color: w.consequenceColor }}
        >
          AUTO →
        </span>
        <span className="text-[12px] leading-snug" style={{ color: "#FFFFFF" }}>
          {w.consequence}
        </span>
      </div>
    </div>
  );
}

function SimpleStat({ icon: Icon, label, value }) {
  return (
    <div
      className="px-3 py-3 flex items-center gap-2.5"
      style={{ background: "#0B0B0B" }}
    >
      <span
        className="inline-flex items-center justify-center w-8 h-8 rounded shrink-0"
        style={{ background: "rgba(255,106,0,0.10)", color: "#FF6A00", border: "1px solid rgba(255,106,0,0.3)" }}
      >
        <Icon className="w-4 h-4" />
      </span>
      <div className="min-w-0">
        <div className="font-mono text-[9px] uppercase tracking-[0.25em] truncate" style={{ color: "#BDBDBD" }}>
          {label}
        </div>
        <div className="font-display text-base font-black text-white leading-tight">
          {value}
        </div>
      </div>
    </div>
  );
}
